'use client';

import { DockAction, DockNav } from '@/components/dock-action';
import { DockPods } from '@/components/dock';
import { CaretLeft } from '@/components/icons';
import { useBack } from '@/components/use-back';
import { useT } from '@/lib/locale-context';

/**
 * The dock on a section screen (docs/MOBILE.md §3): every section of the item
 * is a pushed page on the phone, so the thumb zone holds the way back to the
 * item. The nav region is claimed with a Back circle (the same unwind
 * discipline as `ItemDock`: pop the navigation that opened this screen, or
 * walk to the item when popping would leave the app), and the action slot is
 * claimed EMPTY, so no default create appears over a section of an item. The
 * item's "…" meta sheet stays on the item itself; this screen is one section
 * of it, not the item.
 *
 * `done` puts the one act a screen may push beside Back, in the accent pill:
 * "Done" while the description screen's editor is open (2026-11-02), which
 * commits the text the keyboard keeps a phone from blurring.
 */
export function SectionDock({ itemHref, done }: { itemHref: string; done?: () => void }) {
  return <BackOnlyDock fallbackHref={itemHref} done={done} />;
}

/**
 * The back-only dock, the one thing to do on a screen that has nowhere else
 * to go: a section screen (`SectionDock`), and an item route whose item is
 * missing or hidden (`src/app/items/[itemId]/not-found.tsx`, and the peek's
 * empty render). Claiming the nav region also releases the shell's loading
 * row, which stands on an item route until its page claims the dock
 * (docs/LOADING.md §4.2). Back pops the navigation that opened the screen, or
 * walks to `fallbackHref` when popping would leave the app; while a tap's
 * mount stands over the screen, it dismisses that instead.
 */
export function BackOnlyDock({
  fallbackHref,
  done,
}: {
  fallbackHref: string;
  /** A screen's one pushed act, "Done" (`SectionDock`). */
  done?: () => void;
}) {
  const t = useT();
  const back = useBack(fallbackHref);
  return (
    <>
      <DockNav>
        <DockPods
          left={[
            {
              key: 'back',
              label: t('item.dock.back'),
              icon: <CaretLeft />,
              onClick: back,
              testId: 'dock-back',
            },
          ]}
          right={
            done
              ? [
                  {
                    key: 'done',
                    label: t('item.dock.done'),
                    accent: true,
                    onClick: done,
                    testId: 'dock-done',
                  },
                ]
              : []
          }
        />
      </DockNav>
      <DockAction>{null}</DockAction>
    </>
  );
}
